import React, { useState, useEffect } from 'react';
import { ArrowRight, Check, Shuffle, ChevronLeft, ChevronRight } from 'lucide-react';

interface PartImage {
  display: string;
  default: string;
  withWheel?: string;
}

interface Part {
  id: number;
  name: string;
  price: number;
  images: PartImage;
  previewPosition: { x: number; y: number };
}

interface ScooterPart {
  name: string;
  zIndex: number;
  parts: Part[];
}

const scooterParts: { [key: string]: ScooterPart } = {
  deck: { 
    name: 'Deck',
    zIndex: 10,
    parts: [
      { 
        id: 1, 
        name: 'Blunt Prodigy X Burnt Pipe Deck', 
        price: 89.99, 
        images: {
          display: '/parts/prodigy_x_burnt_pipe_deck_display.png',
          default: '/parts/prodigy_x_burnt_pipe_deck.png'
        },
        previewPosition: { x: -30, y: 160 } 
      },
      // Add more deck parts
    ]
  },
  bars: { 
    name: 'Lenks',
    zIndex: 9,
    parts: [
      { 
        id: 1, 
        name: 'Blunt Reaper Light', 
        price: 69.99, 
        images: {
          display: '/parts/Blunt_Reaper_Light2.png',
          default: '/parts/Blunt_Reaper_Light2.png'
        },
        previewPosition: { x: 25, y: -50 } 
      }
    ]
  },
  grips: { 
    name: 'Käepidemed',
    zIndex: 12,
    parts: [
      { 
        id: 1, 
        name: 'Pro Grips', 
        price: 19.99, 
        images: {
          display: '/parts/grips_display.png',
          default: '/parts/grips.png'
        },
        previewPosition: { x: 25, y: -50 } 
      },
      // Add more grips parts
    ]
  },
  clamp: { 
    name: 'Klamber',
    zIndex: 11,
    parts: [
      { 
        id: 1, 
        name: 'Double Clamp', 
        price: 34.99, 
        images: {
          display: '/parts/clamp_display.png',
          default: '/parts/gsdgdg.png'
        },
        previewPosition: { x: 60, y: 90 } 
      },
      // Add more clamp parts
    ]
  },
  headset: { 
    name: 'Headset',
    zIndex: 8,
    parts: [
      { 
        id: 1, 
        name: 'Standard Headset', 
        price: 29.99, 
        images: {
          display: '/parts/headset_display.png',
          default: '/parts/headset.png'
        },
        previewPosition: { x: 60, y: 90 } 
      },
      // Add more headset parts
    ]
  },
  forks: { 
    name: 'Kahvel',
    zIndex: 7,
    parts: [
      { 
        id: 1, 
        name: 'IHC Fork', 
        price: 49.99, 
        images: {
          display: '/parts/Aztek_Circa3_Fork_display.png',
          default: '/parts/Aztek_Circa3_Fork_Blue5.png',
          withWheel: '/parts/Aztek_Circa3_Fork_Blue6.png'
        },
        previewPosition: { x: 69, y: 155 } 
      },
      // Add more fork parts
    ]
  },
  frontWheel: { 
    name: 'Esiratas',
    zIndex: 6,
    parts: [
      { 
        id: 1, 
        name: '110mm Front Wheel', 
        price: 39.99, 
        images: {
          display: '/parts/wheel_display.png',
          default: '/parts/gsdgdg3.png'
        },
        previewPosition: { x: 75, y: 210 } 
      },
      // Add more wheel parts
    ]
  },
  rearWheel: { 
    name: 'Tagaratas',
    zIndex: 5,
    parts: [
      { 
        id: 1, 
        name: '110mm Rear Wheel', 
        price: 39.99, 
        images: {
          display: '/parts/wheel_display.png',
          default: '/parts/gsdgdg2.png'
        },
        previewPosition: { x: -126, y: 174 } 
      },
      // Add more wheel parts
    ]
  },
  griptape: { 
    name: 'Griptape',
    zIndex: 4,
    parts: [
      { 
        id: 1, 
        name: 'Standard Griptape', 
        price: 14.99, 
        images: {
          display: '/parts/griptape_display.png',
          default: '/parts/griptape.png'
        },
        previewPosition: { x: -30, y: 160 } 
      },
      // Add more griptape parts
    ]
  },
};

const ITEMS_PER_PAGE = 4;

export default function ScooterBuilder() {
  const [selectedParts, setSelectedParts] = useState<{[key: string]: Part}>({});
  const [selectedPartType, setSelectedPartType] = useState<string | null>(null);
  const [showingProducts, setShowingProducts] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const getScaleFactor = () => {
    if (windowWidth > 1536) return 1.2;
    if (windowWidth < 768) return 0.6;
    if (windowWidth < 1024) return 0.8;
    return 1;
  };

  const getPartImage = (type: string, part: Part) => {
    if (type === 'forks' && selectedParts.frontWheel) {
      return part.images.withWheel || part.images.default;
    }
    return part.images.default;
  };

  const ScooterPreview = () => (
    <div className="relative w-full h-full">
      <div 
        className="absolute inset-0 flex items-center justify-center"
        style={{
          transform: `scale(${getScaleFactor()})`,
          transformOrigin: 'center center'
        }}
      >
        {Object.entries(selectedParts).map(([type, part]) => (
          part && (
            <div
              key={type}
              className="absolute"
              style={{
                transform: `translate(${part.previewPosition?.x || 0}px, ${part.previewPosition?.y || 0}px)`,
                zIndex: scooterParts[type].zIndex,
                width: 'auto',
                height: 'auto'
              }}
            >
              <img
                src={getPartImage(type, part)}
                alt={part.name}
                className="w-full h-full object-contain"
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%'
                }}
              />
            </div>
          )
        ))}
        {Object.keys(selectedParts).length === 0 && (
          <img
            src="/parts/scooter-outline.png"
            alt="Scooter Outline"
            className="w-full opacity-30"
            style={{ maxWidth: '600px' }}
          />
        )}
      </div>
    </div>
  );

  const handlePartSelect = (part: Part, type: string) => {
    setSelectedParts(prev => ({
      ...prev,
      [type]: part
    }));
    setSelectedPartType(null);
    setShowingProducts(false);
    setCurrentPage(1);
  };

  const handleCategoryClick = (type: string) => {
    setSelectedPartType(type);
    setShowingProducts(true);
    setCurrentPage(1);
  };

  const randomizeParts = () => {
    const randomized = Object.entries(scooterParts).reduce((acc, [type, category]) => {
      const randomIndex = Math.floor(Math.random() * category.parts.length);
      acc[type] = category.parts[randomIndex];
      return acc;
    }, {});
    setSelectedParts(randomized);
  };

  const resetAll = () => {
    setSelectedParts({});
    setSelectedPartType(null);
    setShowingProducts(false);
    setCurrentPage(1);
  };

  const totalPrice = Object.values(selectedParts)
    .reduce((sum: number, part: Part) => sum + part.price, 0);

  const getPaginatedParts = () => {
    if (!selectedPartType) return [];
    const parts = scooterParts[selectedPartType].parts;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return parts.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const totalPages = selectedPartType 
    ? Math.ceil(scooterParts[selectedPartType].parts.length / ITEMS_PER_PAGE)
    : 0;

  return (
    <div className="container mx-auto py-12 px-4 md:px-6">
      <h1 className="text-4xl font-bold mb-8">Ehita Oma Tõuks</h1>
      
      <div className="mb-12 overflow-x-auto">
        <div className="relative min-w-[800px]">
          <div className="absolute left-0 top-1/2 w-full h-0.5 bg-gray-200 -translate-y-1/2">
            <div 
              className="h-full bg-[#4FC1E9] transition-all duration-500"
              style={{
                width: `${(Object.keys(selectedParts).length / Object.keys(scooterParts).length) * 100}%`
              }}
            ></div>
          </div>
          <div className="relative flex justify-between">
            {Object.entries(scooterParts).map(([type, category], index) => (
              <div 
                key={type} 
                className="flex flex-col items-center"
              >
                <div 
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center z-10
                    transition-all duration-500
                    ${selectedParts[type] ? 'bg-[#4FC1E9] text-white' : 'bg-white border-2 border-gray-200 text-gray-400'}
                  `}
                >
                  {selectedParts[type] ? <Check className="w-4 h-4" /> : index + 1}
                </div>
                <span className={`
                  mt-2 text-sm font-medium
                  ${selectedParts[type] ? 'text-[#4FC1E9]' : 'text-gray-500'}
                `}>
                  {category.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-7 bg-white rounded-xl shadow-lg p-6">
          <div className="aspect-square rounded-lg overflow-hidden mb-6 bg-gray-50">
            <ScooterPreview />
          </div>
          <div className="space-y-4">
            <h3 className="text-xl font-bold">Valitud osad:</h3>
            {Object.entries(selectedParts).map(([type, part]) => (
              <div key={type} className="flex justify-between items-center">
                <span className="font-medium">{scooterParts[type].name}:</span>
                <span className="text-gray-600">{part ? part.name : 'Pole valitud'}</span>
              </div>
            ))}
            <div className="border-t pt-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-xl font-bold">Kokku:</span>
                <span className="text-xl font-bold text-[#4FC1E9]">{totalPrice.toFixed(2)}€</span>
              </div>
            </div>
          </div>
        </div>

        <div className="md:col-span-5">
          <button 
            onClick={randomizeParts}
            className="w-full bg-[#4FC1E9] text-white py-3 rounded-lg hover:bg-[#3ba8d0] mb-4 flex items-center justify-center"
          >
            <Shuffle className="w-5 h-5 mr-2" />
            Juhuslikud osad
          </button>

          {showingProducts && selectedPartType && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold">Vali {scooterParts[selectedPartType].name}</h3>
                <button 
                  onClick={() => {
                    setShowingProducts(false);
                    setSelectedPartType(null);
                    setCurrentPage(1);
                  }}
                  className="text-gray-500 hover:text-[#4FC1E9]"
                >
                  Tagasi
                </button>
              </div>
              <div className="space-y-4">
                {getPaginatedParts().map((part) => (
                  <div
                    key={part.id}
                    className="flex items-center p-4 rounded-lg hover:bg-gray-100 cursor-pointer border"
                    onClick={() => handlePartSelect(part, selectedPartType)}
                  >
                    <div className="w-32 h-32 flex items-center justify-center bg-white rounded-lg overflow-hidden">
                      <img 
                        src={part.images.display} 
                        alt={part.name} 
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                    <div className="flex-1 ml-4">
                      <div className="font-medium">{part.name}</div>
                      <div className="text-[#4FC1E9] text-lg font-bold mt-1">{part.price}€</div>
                    </div>
                  </div>
                ))}

                {totalPages > 1 && (
                  <div className="flex justify-center items-center gap-4 mt-6">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <span className="text-sm text-gray-600">
                      Leht {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {!showingProducts && (
            <div className="grid grid-cols-2 gap-4">
              {Object.entries(scooterParts).map(([type, category]) => (
                <div 
                  key={type} 
                  className="bg-white rounded-lg shadow p-4 cursor-pointer hover:ring-2 hover:ring-[#4FC1E9]"
                  onClick={() => handleCategoryClick(type)}
                >
                  <h3 className="font-bold mb-2">{category.name}</h3>
                  <button className="w-full text-sm text-gray-600 hover:text-[#4FC1E9]">
                    {selectedParts[type] ? 'MUUDA' : 'VALI OSA'}
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <div className="mt-8 space-y-4">
            <button 
              onClick={resetAll}
              className="w-full py-2 text-red-500 hover:text-red-600"
            >
              TÜHISTA KÕIK
            </button>
            <button className="w-full bg-[#4FC1E9] text-white py-3 rounded hover:bg-[#3ba8d0]">
              LISA KORVI
            </button>
          </div>
        </div>
      </div>

      <div className="bg-[#4FC1E9]/10 rounded-xl p-6 mt-12">
        <h2 className="text-2xl font-bold mb-4 text-[#4FC1E9]">Ehita oma unistuste tõuks!</h2>
        <p className="text-gray-700 mb-4">
          Meie tõuksi konfiguraator võimaldab sul valida täpselt need komponendid, mis sobivad sinu sõidustiiliga. 
          Kõik valitud osad on omavahel ühilduvad ja professionaalselt testitud, tagades maksimaalse ohutuse ja 
          sõidunaudingu.
        </p>
        <p className="text-gray-700">
          Vali iga komponendi jaoks sobiv variant ja meie eksperdid panevad sinu tõuksi kokku, 
          kasutades ainult kvaliteetseid tööriistu ja tehnikaid. Iga kokkupandud tõuks läbib põhjaliku 
          kvaliteedikontrolli enne kliendini jõudmist.
        </p>
      </div>
    </div>
  );
}